/**
 * AuthContext – Login, Logout, Session-Verwaltung, Benachrichtigungs-Count.
 *
 * Verwaltet den eingeloggten Benutzer (AuthUser) und stellt Login/Logout/Session-Check
 * im gesamten System bereit. Ein Ping-Intervall prüft regelmäßig die Session-Gültigkeit
 * und Benachrichtigungen (hasNews/newsCount aus dem Ping).
 *
 * newsCount wird als Zahl exponiert, damit der NotificationWatcher Änderungen
 * erkennen und Growl/Sound/Browser-Notifications auslösen kann.
 * unpushedCount zeigt an, wie viele Notifications noch nicht zugestellt wurden
 * (pushed=0) – der Watcher nutzt das, um zu entscheiden ob ein Fetch nötig ist.
 * hasUnseenChangelog zeigt an, ob eine ungelesene Changelog-Notification existiert
 * (für den Badge im UserMenu). Einzige Quelle der Wahrheit (kein localStorage).
 * notificationSound/Sticky werden aus dem Ping gelesen (system_users_notifications)
 * und vom NotificationWatcher für Sound- und Sticky-Verhalten genutzt.
 *
 * Zusammenhänge:
 *   - index.tsx               → AuthProvider umschließt die gesamte App
 *   - App.tsx                 → useAuth() für Login-Gating (kein User → LoginPage)
 *   - NotificationWatcher.tsx → liest newsCount, löst Growl/Sound/Browser-Notification aus
 *   - notifier.php            → Backend-API (action=session, login, logout, ping)
 *   - types.ts                → AuthUser-Interface
 *   - services/api.ts         → registerLogout() für automatisches 401-Handling im API-Wrapper
 *
 * Ablauf:
 *   1. Mount → checkSession() → notifier.php?action=session → user setzen
 *   2. Nach Login → Ping-Intervall starten (alle 10s session + news prüfen)
 *   3. Ping erkennt abgelaufene Session → user = null → automatischer Logout
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import type { AuthUser } from '@/types';
import { appConfig } from '@/config';
import { registerLogout } from '@/services/api';

/** Endpunkt für Session/Login/Logout/Ping. */
const API_NOTIFIER = 'data/notifier.php';
/** Intervall für Session-Ping (ms). */
const PING_INTERVAL_MS = 10_000;

type AuthState = {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
  hasNews: boolean;
  /** Anzahl ungelesener Benachrichtigungen (0 = keine). */
  newsCount: number;
  /** Anzahl noch nicht zugestellter Notifications (pushed=0, dismissed=0). */
  unpushedCount: number;
  /** true wenn eine ungelesene Changelog-Notification existiert (Badge im UserMenu). */
  hasUnseenChangelog: boolean;
  /** 1=Sound bei neuen Notifications, 0=stumm. Aus system_users_notifications. */
  notificationSound: number;
  /** 1=Growls bleiben bis weggeklickt, 0=auto-dismiss. Aus system_users_notifications. */
  notificationSticky: number;
};

type AuthContextValue = AuthState & {
  login: (login: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  checkSession: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

const fetchOpts: RequestInit = { credentials: 'include' };

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasNews, setHasNews] = useState(false);
  const [newsCount, setNewsCount] = useState(0);
  const [unpushedCount, setUnpushedCount] = useState(0);
  const [hasUnseenChangelog, setHasUnseenChangelog] = useState(false);
  const [notificationSound, setNotificationSound] = useState(1);
  const [notificationSticky, setNotificationSticky] = useState(1);
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const checkSession = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_NOTIFIER}?action=session`, fetchOpts);
      if (res.ok) {
        const data = await res.json();
        setUser(data.user ?? null);
        if (data.systemName !== undefined) {
          document.title = (String(data.systemName || '').trim() || appConfig.appName || 'Xeno Admin');
        }
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  const login = useCallback(async (loginValue: string, password: string): Promise<{ success: boolean; error?: string }> => {
    setError(null);
    try {
      const res = await fetch(`${API_NOTIFIER}?action=login`, {
        ...fetchOpts,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ login: loginValue.trim(), password }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success && data.user) {
        setUser(data.user);
        if (data.systemName !== undefined) {
          document.title = (String(data.systemName || '').trim() || appConfig.appName || 'Xeno Admin');
        }
        return { success: true };
      }
      return { success: false, error: (data.error as string) || 'Anmeldung fehlgeschlagen.' };
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Netzwerkfehler';
      return { success: false, error: msg };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch(`${API_NOTIFIER}?action=logout`, { ...fetchOpts, method: 'POST' });
    } finally {
      setUser(null);
      setHasNews(false);
      setNewsCount(0);
      setUnpushedCount(0);
      setHasUnseenChangelog(false);
      setNotificationSound(1);
      setNotificationSticky(1);
    }
  }, []);

  /* Logout-Funktion beim API-Wrapper registrieren, damit api()/apiPost()
     bei 401 automatisch ausloggen können (services/api.ts). */
  useEffect(() => {
    registerLogout(logout);
  }, [logout]);

  useEffect(() => {
    if (!user) {
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      return;
    }
    const ping = async () => {
      try {
        const res = await fetch(`${API_NOTIFIER}?action=ping`, fetchOpts);
        if (!res.ok) return;
        const data = await res.json().catch(() => ({}));
        if (data.sessionActive === false) {
          setUser(null);
          setHasNews(false);
          setNewsCount(0);
          setUnpushedCount(0);
          setHasUnseenChangelog(false);
          setNotificationSound(1);
          setNotificationSticky(1);
          if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
            pingIntervalRef.current = null;
          }
          return;
        }
        const count = typeof data.newsCount === 'number' ? data.newsCount : 0;
        setHasNews(count > 0);
        setNewsCount(count);
        setUnpushedCount(typeof data.unpushedCount === 'number' ? data.unpushedCount : 0);
        setHasUnseenChangelog(!!data.hasUnseenChangelog);
        setNotificationSound(typeof data.notificationSound === 'number' ? data.notificationSound : 1);
        setNotificationSticky(typeof data.notificationSticky === 'number' ? data.notificationSticky : 1);
      } catch {
        // Netzwerkfehler: Session-Check überspringen, hasNews/newsCount unverändert
      }
    };
    ping();
    pingIntervalRef.current = setInterval(ping, PING_INTERVAL_MS);
    return () => {
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
    };
  }, [user]);

  const value: AuthContextValue = {
    user,
    loading,
    error,
    hasNews,
    newsCount,
    unpushedCount,
    hasUnseenChangelog,
    notificationSound,
    notificationSticky,
    login,
    logout,
    checkSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

/**
 * Pruefen ob der aktuelle Benutzer ein bestimmtes Recht hat.
 * Superadmin (permissions=['*'] oder sa=1) hat immer alle Rechte.
 *
 * @param permissionName Maschinenname des Rechts (z. B. 'help_edit')
 * @returns true wenn berechtigt oder Superadmin
 */
export function usePermission(permissionName: string): boolean {
  const { user } = useAuth();
  if (!user) return false;
  if (user.sa === 1) return true;
  const perms = user.permissions ?? [];
  return perms.includes('*') || perms.includes(permissionName);
}
